import * as THREE from 'three';
import { EventBus } from '../core/EventBus';

export type CollectibleType =
  | 'health'
  | 'ammo'
  | 'intel'
  | 'shield'
  // Temporary buffs
  | 'buff_damage'
  | 'buff_firerate'
  | 'buff_speed'
  // New weapon unlocks
  | 'weapon_railgun'
  | 'weapon_plasma'
  | 'weapon_missiles'
  // Permanent upgrades
  | 'perm_firerate'
  | 'perm_magazine'
  | 'perm_projspeed';

export interface CollectibleConfig {
  position: THREE.Vector3;
  type: CollectibleType;
  value: number; // Amount to restore or points for intel
  respawnTime?: number; // Time to respawn in seconds, 0 = no respawn
}

const COLLECTIBLE_COLORS: Record<CollectibleType, number> = {
  health: 0x00ff00,
  ammo: 0xff00ff,
  intel: 0xffff00,
  shield: 0x00ffff,
  // Buffs - warm colors
  buff_damage: 0xffa500,
  buff_firerate: 0xffcc00,
  buff_speed: 0xff6600,
  // Weapons - unique colors
  weapon_railgun: 0x00ff88,
  weapon_plasma: 0x8000ff,
  weapon_missiles: 0xff4400,
  // Permanent - gold/platinum
  perm_firerate: 0xffd700,
  perm_magazine: 0xe5e4e2,
  perm_projspeed: 0xb9f2ff,
};

const COLLECTIBLE_SHAPES: Record<CollectibleType, string> = {
  health: 'cross',
  ammo: 'diamond',
  intel: 'cube',
  shield: 'sphere',
  // Buffs - hexagon
  buff_damage: 'hexagon',
  buff_firerate: 'hexagon',
  buff_speed: 'hexagon',
  // Weapons - star
  weapon_railgun: 'star',
  weapon_plasma: 'star',
  weapon_missiles: 'star',
  // Permanent - crystal
  perm_firerate: 'crystal',
  perm_magazine: 'crystal',
  perm_projspeed: 'crystal',
};

export class Collectible {
  private scene: THREE.Scene;
  private mesh: THREE.Group;
  private config: CollectibleConfig;
  private isCollected: boolean = false;
  private respawnTimer: number = 0;
  private readonly PICKUP_RADIUS = 2;
  private readonly FLOAT_HEIGHT = 1.5;
  private readonly BOB_SPEED = 2;
  private readonly BOB_AMOUNT = 0.3;
  private readonly SPIN_SPEED = 1.5;

  constructor(scene: THREE.Scene, config: CollectibleConfig) {
    this.scene = scene;
    this.config = config;

    this.mesh = this.createMesh();
    this.mesh.position.copy(config.position);
    this.mesh.position.y = this.FLOAT_HEIGHT;
    scene.add(this.mesh);
  }

  private createMesh(): THREE.Group {
    const group = new THREE.Group();
    const color = COLLECTIBLE_COLORS[this.config.type];
    const shape = COLLECTIBLE_SHAPES[this.config.type];

    let geometry: THREE.BufferGeometry;

    switch (shape) {
      case 'cross':
        // Health cross made of two boxes
        const crossGroup = new THREE.Group();
        const crossMat = new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: 1,
        });
        const hBar = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.4, 0.4), crossMat);
        const vBar = new THREE.Mesh(new THREE.BoxGeometry(0.4, 1.2, 0.4), crossMat.clone());
        crossGroup.add(hBar);
        crossGroup.add(vBar);
        group.add(crossGroup);
        geometry = new THREE.BoxGeometry(1, 1, 1); // Dummy for glow
        break;

      case 'diamond':
        geometry = new THREE.OctahedronGeometry(0.6);
        break;

      case 'cube':
        geometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
        break;

      case 'sphere':
        geometry = new THREE.SphereGeometry(0.5, 16, 16);
        break;

      case 'hexagon':
        // Flat hexagonal prism for buffs
        geometry = new THREE.CylinderGeometry(0.6, 0.6, 0.3, 6);
        break;

      case 'star':
        // 5-pointed star extruded (using octahedron as base, stretched)
        geometry = new THREE.OctahedronGeometry(0.7);
        // Star-like appearance via scaling
        break;

      case 'crystal':
        // Elongated crystal shape
        geometry = new THREE.ConeGeometry(0.4, 1.2, 6);
        break;

      default:
        geometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
    }

    // Main shape (if not cross which is already added)
    if (shape !== 'cross') {
      const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 1,
        metalness: 0.8,
        roughness: 0.2,
      });
      const mainMesh = new THREE.Mesh(geometry, material);
      group.add(mainMesh);
    }

    // Outer glow ring
    const glowRing = new THREE.Mesh(
      new THREE.TorusGeometry(1, 0.05, 8, 32),
      new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 2,
        transparent: true,
        opacity: 0.6,
      })
    );
    glowRing.rotation.x = Math.PI / 2;
    group.add(glowRing);

    // Ground indicator
    const groundRing = new THREE.Mesh(
      new THREE.RingGeometry(0.8, 1, 32),
      new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
      })
    );
    groundRing.rotation.x = -Math.PI / 2;
    groundRing.position.y = -this.FLOAT_HEIGHT + 0.02;
    group.add(groundRing);

    // Particle effect (simple floating particles)
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 4, 4),
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: 2,
        })
      );
      const angle = (i / particleCount) * Math.PI * 2;
      particle.position.set(
        Math.cos(angle) * 0.8,
        (Math.random() - 0.5) * 0.5,
        Math.sin(angle) * 0.8
      );
      particle.userData.angle = angle;
      particle.userData.isParticle = true;
      group.add(particle);
    }

    return group;
  }

  public update(dt: number, playerPosition: THREE.Vector3): boolean {
    if (this.isCollected) {
      // Handle respawn timer
      if (this.config.respawnTime && this.config.respawnTime > 0) {
        this.respawnTimer -= dt;
        if (this.respawnTimer <= 0) {
          this.respawn();
        }
      }
      return false;
    }

    // Animate
    const time = performance.now() / 1000;

    // Bob up and down
    this.mesh.position.y = this.FLOAT_HEIGHT + Math.sin(time * this.BOB_SPEED) * this.BOB_AMOUNT;

    // Spin
    this.mesh.rotation.y += this.SPIN_SPEED * dt;

    // Animate particles
    this.mesh.traverse((child) => {
      if (child.userData.isParticle) {
        const angle = child.userData.angle + time * 2;
        child.position.x = Math.cos(angle) * 0.8;
        child.position.z = Math.sin(angle) * 0.8;
        child.position.y = Math.sin(time * 3 + child.userData.angle) * 0.3;
      }
    });

    // Check for player pickup
    const distance = this.mesh.position.distanceTo(playerPosition);
    if (distance < this.PICKUP_RADIUS) {
      this.collect();
      return true;
    }

    return false;
  }

  private collect(): void {
    this.isCollected = true;
    this.mesh.visible = false;

    if (this.config.respawnTime && this.config.respawnTime > 0) {
      this.respawnTimer = this.config.respawnTime;
    }

    // Emit appropriate event based on type
    switch (this.config.type) {
      case 'health':
        EventBus.emit('player:healed', { amount: this.config.value });
        break;

      case 'ammo':
        EventBus.emit('player:ammo', { amount: this.config.value });
        EventBus.emit('score:added', { points: 50, reason: 'Ammo pickup' });
        break;

      case 'intel':
        EventBus.emit('score:added', { points: this.config.value, reason: 'Intel collected' });
        break;

      case 'shield':
        EventBus.emit('upgrade:buff', { type: 'shield' });
        EventBus.emit('score:added', { points: 100, reason: 'Shield activated' });
        break;

      // Temporary buffs
      case 'buff_damage':
        EventBus.emit('upgrade:buff', { type: 'damage' });
        EventBus.emit('score:added', { points: 100, reason: 'Damage boost' });
        break;

      case 'buff_firerate':
        EventBus.emit('upgrade:buff', { type: 'firerate' });
        EventBus.emit('score:added', { points: 100, reason: 'Fire rate boost' });
        break;

      case 'buff_speed':
        EventBus.emit('upgrade:buff', { type: 'speed' });
        EventBus.emit('score:added', { points: 100, reason: 'Speed boost' });
        break;

      // Weapon unlocks
      case 'weapon_railgun':
        EventBus.emit('upgrade:weapon', { weapon: 'railgun' });
        EventBus.emit('score:added', { points: 500, reason: 'Railgun unlocked!' });
        break;

      case 'weapon_plasma':
        EventBus.emit('upgrade:weapon', { weapon: 'plasmaCannon' });
        EventBus.emit('score:added', { points: 500, reason: 'Plasma Cannon unlocked!' });
        break;

      case 'weapon_missiles':
        EventBus.emit('upgrade:weapon', { weapon: 'homingMissiles' });
        EventBus.emit('score:added', { points: 500, reason: 'Homing Missiles unlocked!' });
        break;

      // Permanent upgrades
      case 'perm_firerate':
        EventBus.emit('upgrade:permanent', { type: 'fireRateLevel' });
        EventBus.emit('score:added', { points: 250, reason: 'Fire rate upgraded!' });
        break;

      case 'perm_magazine':
        EventBus.emit('upgrade:permanent', { type: 'magazineSizeLevel' });
        EventBus.emit('score:added', { points: 250, reason: 'Magazine size upgraded!' });
        break;

      case 'perm_projspeed':
        EventBus.emit('upgrade:permanent', { type: 'projectileSpeedLevel' });
        EventBus.emit('score:added', { points: 250, reason: 'Projectile speed upgraded!' });
        break;
    }

    // Create pickup effect
    this.createPickupEffect();
  }

  private createPickupEffect(): void {
    const color = COLLECTIBLE_COLORS[this.config.type];
    const position = this.mesh.position.clone();

    // Expanding ring effect
    const ringGeom = new THREE.TorusGeometry(0.5, 0.1, 8, 32);
    const ringMat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 3,
      transparent: true,
      opacity: 1,
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.rotation.x = Math.PI / 2;
    ring.position.copy(position);
    this.scene.add(ring);

    // Animate ring expansion
    const startTime = performance.now();
    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      const scale = 1 + elapsed * 5;
      ring.scale.setScalar(scale);
      ringMat.opacity = 1 - elapsed * 2;

      if (elapsed < 0.5) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(ring);
      }
    };
    requestAnimationFrame(animate);

    // Particle burst
    for (let i = 0; i < 12; i++) {
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 4, 4),
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: 2,
          transparent: true,
        })
      );
      particle.position.copy(position);

      const angle = (i / 12) * Math.PI * 2;
      const velocity = new THREE.Vector3(
        Math.cos(angle) * 5,
        2 + Math.random() * 3,
        Math.sin(angle) * 5
      );

      this.scene.add(particle);

      const animateParticle = () => {
        velocity.y -= 10 * 0.016;
        particle.position.add(velocity.clone().multiplyScalar(0.016));
        const mat = particle.material as THREE.MeshStandardMaterial;
        mat.opacity -= 0.03;

        if (particle.position.y < 0 || mat.opacity <= 0) {
          this.scene.remove(particle);
        } else {
          requestAnimationFrame(animateParticle);
        }
      };
      requestAnimationFrame(animateParticle);
    }
  }

  private respawn(): void {
    this.isCollected = false;
    this.mesh.visible = true;

    // Respawn effect - fade in
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material as THREE.MeshStandardMaterial;
        if (mat.transparent !== undefined) {
          mat.opacity = 0;
          mat.transparent = true;
        }
      }
    });

    const fadeIn = () => {
      let allFaded = true;
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material as THREE.MeshStandardMaterial;
          if (mat.opacity < 1) {
            mat.opacity = Math.min(1, mat.opacity + 0.05);
            allFaded = false;
          }
        }
      });

      if (!allFaded) {
        requestAnimationFrame(fadeIn);
      }
    };
    requestAnimationFrame(fadeIn);
  }

  public isActive(): boolean {
    return !this.isCollected;
  }

  public getPosition(): THREE.Vector3 {
    return this.config.position.clone();
  }

  public getType(): CollectibleType {
    return this.config.type;
  }

  public destroy(): void {
    this.scene.remove(this.mesh);
  }
}
